import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, createObjectWithCondition, downloadFile, getPostfixWithRobotId, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "@/constants/api_endpoints";
import { CorrectionType } from "@/types/smcTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";
import {
	ActiveRobotVersionType,
	DaysType,
	ExceptionType,
	IAddingRobotAction,
	IAddingRobotException,
	IAddingRobotResponse,
	IChangeServiceData,
	IDataRobotList,
	IEditRobotProps,
	IExportRobotProps,
	IImportRobotProps,
	IInterval,
	IModelTtsConfig,
	IRobotResponse,
	ISearchInRobotResponse,
	ISesRobotState,
	RobotActionEventType,
	ServiceDataType,
} from "@/types/ses/sesRobotTypes";
import {
	ActionVariantType,
	AtomicCondition,
	AtomicConditionType,
	CategoryCondition,
	ConditionType,
	IActionInternalAsk,
	IConditionConfirmation,
	IConditionData,
	IConditionVariable,
	IMulticondition,
} from "@/types/ses/sesItemConfigTypes";
import { ServiceTypeModelRobot, ServiceTypeRobot } from "@/types/cloudTypes";
import { TIME_INTERVAL } from "@/constants/defaultValues";
import { YesNoType } from "@/types/qas/qasTypes";

const initialState: ISesRobotState = {
	robotList: {
		data: [],
		activeRobotId: null,
		activeRobotVersion: "draft",
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	robot: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		hasProd: false,
		hasBackup: false,
	},
	addingRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
	},
	editingRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	commitConfigRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	applyingRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	restorationRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	clearDraftingRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	importRobot: {
		progress: 0,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	exportRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	searchInRobot: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		data: null,
	},
};

// список роботов
export const getRobotList = createAsyncThunk("sesRobot/list", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IDataRobotList[] | IResponse> = await axiosApi.get(`${URL_SES}/${ENDPOINTS_SES.ROBOT_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// данные робота
export const getRobot = createAsyncThunk("sesRobot/data", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IRobotResponse | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.ROBOT_GET}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление робота
export const addRobot = createAsyncThunk("sesRobot/add", async (robotName: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddingRobotResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ROBOT_ADD}`, {
			name: robotName,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение данных робота
export const editRobot = createAsyncThunk("sesRobot/edit", async ({ robotId, data }: IEditRobotProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ROBOT_EDIT}/${robotId}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление робота
export const deleteRobot = createAsyncThunk("sesRobot/delete", async (robotId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_SES}/${ENDPOINTS_SES.ROBOT_DELETE}/${robotId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// применение настроек робота
export const commitConfigRobot = createAsyncThunk("sesRobot/commitConfig", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.ROBOT_COMMIT_CONFIG}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// применение черновика робота в прод
export const applyRobot = createAsyncThunk("sesRobot/apply", async (robotId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ROBOT_APPLY}/${robotId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// восстановление робота из бэкапа
export const restoreRobot = createAsyncThunk("sesRobot/restore", async (robotId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ROBOT_RESTORE}/${robotId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// обнуление черновика роботом прода
export const clearDraftRobot = createAsyncThunk("sesRobot/clearDraft", async (robotId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ROBOT_CLEAR_DRAFT}/${robotId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// импорт робота из .zip файла
export const importRobot = createAsyncThunk("sesRobot/import", async ({ robotId, formData }: IImportRobotProps, { dispatch, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.ROBOT_IMPORT}/${robotId}`,
			{
				zip: formData.get("file"),
			},
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent) => {
					if (progressEvent.progress) {
						const progress = Math.round(progressEvent.progress * 100);
						dispatch(changeProgressImportRobot(progress));
					}
				},
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// экспорт робота (.zip файл)
export const exportRobot = createAsyncThunk("sesRobot/export", async ({ robotId, robotName, qas, models }: IExportRobotProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.ROBOT_EXPORT}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`,
			{
				responseType: "blob",
				params: {
					qas,
					models,
				},
			}
		);
		if (response.data instanceof Blob) {
			downloadFile(response.data, `${robotName}.zip`);
		} else return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// глобальный поиск по сценарию
export const searchInRobot = createAsyncThunk("sesRobot/search", async (text: string, { getState, rejectWithValue }) => {
	const robotListStore = (getState() as RootState).sesRobot.robotList;
	try {
		const response: AxiosResponse<ISearchInRobotResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.ROBOT_SEARCH}/${getPostfixWithRobotId(robotListStore.activeRobotVersion, robotListStore.activeRobotId)}`,
			{
				text,
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesRobotSlice = createSlice({
	name: "sesRobot",
	initialState,
	reducers: {
		// добавление id активного робота
		addingActiveRobotId: (state, action: PayloadAction<string>) => {
			state.robotList.activeRobotId = action.payload;
		},
		// изменение активной версии робота
		changeActiveRobotVersion: (state, action: PayloadAction<ActiveRobotVersionType>) => {
			state.robotList.activeRobotVersion = action.payload;
		},
		// добавление возможных версий робота
		addingRobotPossibleVersions: (state, action: PayloadAction<{ hasProd: boolean; hasBackup: boolean }>) => {
			state.robot.hasProd = action.payload.hasProd;
			state.robot.hasBackup = action.payload.hasBackup;
		},
		// изменение возможных версий робота в списке
		changeRobotPossibleVersionsInList: (state, action: PayloadAction<{ hasProd?: boolean; hasBackup?: boolean }>) => {
			state.robotList.data = state.robotList.data.map((robotItem) => {
				if (robotItem.id === state.robotList.activeRobotId) {
					return {
						...robotItem,
						hasProd: action.payload.hasProd !== undefined ? action.payload.hasProd : robotItem.hasProd,
						hasBackup: action.payload.hasBackup !== undefined ? action.payload.hasBackup : robotItem.hasBackup,
					};
				} else return robotItem;
			});
		},
		// изменение имени робота
		editRobotName: (state, action: PayloadAction<{ robotId: string; robotName: string }>) => {
			state.robotList.data = state.robotList.data.map((robot) => {
				if (robot.id === action.payload.robotId) return { ...robot, name: action.payload.robotName };
				else return robot;
			});
		},
		// -------------------------------------------------------------------------
		// добавление действия робота
		addingRobotAction: (state, action: PayloadAction<IAddingRobotAction>) => {
			if (state.robot.data) {
				// если есть событие действия
				if (action.payload.actionEvent in state.robot.data.actions) {
					// и если есть канал
					if (action.payload.channel in state.robot.data.actions[action.payload.actionEvent]) {
						state.robot.data.actions[action.payload.actionEvent][action.payload.channel].push(action.payload.action); // добавляем действие
					}
					// если нет канала - создаем
					else {
						state.robot.data.actions[action.payload.actionEvent][action.payload.channel] = [action.payload.action];
					}
				}
				// если нет события действия - создаем событие и канал
				else {
					state.robot.data.actions[action.payload.actionEvent] = {
						[action.payload.channel]: [action.payload.action],
					};
				}
			}
		},
		// замена действия робота
		replaceActionRobot: (state, actionRedux: PayloadAction<{ actionEvent: RobotActionEventType; channel: string; idx: number; action: ActionVariantType }>) => {
			if (state.robot.data) {
				const { actionEvent, channel, idx, action } = actionRedux.payload;
				state.robot.data.actions[actionEvent][channel][idx] = action;
			}
		},
		// изменение данных действия события (experiment)
		changeRobotEventActionData: (
			state,
			actionRedux: PayloadAction<{ actionEvent: RobotActionEventType; channel: string; idx: number; action: Partial<ActionVariantType> }>
		) => {
			if (state.robot.data) {
				const { actionEvent, channel, idx, action } = actionRedux.payload;
				state.robot.data.actions[actionEvent][channel][idx] = {
					...state.robot.data.actions[actionEvent][channel][idx],
					...(action as ActionVariantType),
				};
			}
		},
		// удаление действия робота
		deleteActionRobot: (state, action: PayloadAction<{ actionEvent: RobotActionEventType; channel: string; idx: number }>) => {
			state.robot.data?.actions[action.payload.actionEvent][action.payload.channel].splice(action.payload.idx, 1);
		},
		// изменение последовательности действий робота
		replaceRobotActionList: (state, action: PayloadAction<{ actionEvent: RobotActionEventType; channel: string; actionList: ActionVariantType[] }>) => {
			if (state.robot.data) {
				state.robot.data.actions[action.payload.actionEvent][action.payload.channel] = action.payload.actionList;
			}
		},
		// -------------------------------------------------------------------------
		// добавление данных категории робота
		addCategoryDataRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, data, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			// Инициализация categories
			if (!actionBlock.categories) {
				actionBlock.categories = { [categoryId]: [[data as CategoryCondition]] };
				return;
			}

			if (!(categoryId in actionBlock.categories)) {
				actionBlock.categories[categoryId] = [[data as CategoryCondition]];
				return;
			}

			const categoryBlocks = actionBlock.categories[categoryId];

			// Добавление в существующий categoryBlock
			if (categoryBlockIdx < categoryBlocks.length) {
				const categoryBlock = categoryBlocks[categoryBlockIdx];

				// Добавление в OR-блок
				if (typeof orBlockIndex === "number" && categoryBlock[orBlockIndex]?.type === "or") {
					(categoryBlock[orBlockIndex] as IMulticondition).value.push(data as AtomicCondition);
					return;
				}

				// Добавление в существующий AND-блок
				categoryBlock.push(data as CategoryCondition);
				return;
			}

			// Новый categoryBlock
			categoryBlocks.push([data as CategoryCondition]);
		},

		// изменение типа данных категории робота
		changeTypeCategoryRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.robot.data) return;

			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const actionItem = state.robot.data.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionItem) return;

			const categoryBlocks = actionItem.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
			}
		},

		// изменение данных категории робота
		changeCategoryDataRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				data: IConditionData | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, data, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			const categoryBlocks = actionBlock.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, ...(condition.value as CategoryCondition[]));
					}
				}
				return;
			}

			// Обычная замена (AND)
			categoryBlock[categoryIdx] = data as CategoryCondition;
		},

		// удаление данных категории робота
		deleteCategoryDataRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, orBlockIndex } = action.payload;
			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk;
			if (!actionBlock?.categories) return;

			const categoryBlocks = actionBlock.categories[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = categoryBlock[categoryIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						categoryBlock.splice(
							categoryIdx,
							1,
							...(orCondition.value[0] as unknown as (IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition)[])
						);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (categoryBlock.length > 1) {
				categoryBlock.splice(categoryIdx, 1);
			} else {
				// если остался один блок — удаляем весь блок
				if (categoryBlocks.length > 1) {
					categoryBlocks.splice(categoryBlockIdx, 1);
				} else {
					// если остался только один блок и один элемент — удаляем категорию целиком
					delete actionBlock.categories[categoryId];
					actionBlock.longContext = actionBlock.longContext?.filter((id) => id !== categoryId);
				}
			}
		},

		// изменение флага запроса в QAS с учетом прошлого контекста
		changeLongContextRobot: (
			state,
			action: PayloadAction<{ actionEvent: RobotActionEventType; channel: string; channelIdx: number; categoryId: string; flag: boolean }>
		) => {
			if (state.robot.data) {
				const actionInternalAsk = state.robot.data.actions[action.payload.actionEvent][action.payload.channel][action.payload.channelIdx] as IActionInternalAsk;
				// если стоит галочка - добавляем в список, иначе убираем
				if (action.payload.flag) {
					if (Array.isArray(actionInternalAsk.longContext)) actionInternalAsk.longContext.push(action.payload.categoryId);
					else actionInternalAsk.longContext = [action.payload.categoryId];
				} else {
					(state.robot.data.actions[action.payload.actionEvent][action.payload.channel][action.payload.channelIdx] as IActionInternalAsk).longContext = (
						state.robot.data.actions[action.payload.actionEvent][action.payload.channel][action.payload.channelIdx] as IActionInternalAsk
					).longContext?.filter((categoryId) => categoryId !== action.payload.categoryId);
				}
			}
		},
		// -------------------------------------------------------------------------
		// добавление условия запуска действия робота
		addConditionRunActionRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, conditionBlockIdx, data, actionEvent, orBlockIndex } = action.payload;
			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent][channel]?.[channelIdx];
			if (!actionBlock) return;

			if (!actionBlock.conditions) {
				actionBlock.conditions = [[data]];
				return;
			}

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			actionBlock.conditions.push([data]);
		},
		// изменение типа условия запуска действия робота
		changeTypeConditionRunActionRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent]?.[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			const targetCondition = conditionBlock[conditionIdx];
			if (conditionType === "or") {
				conditionBlock[conditionIdx] = { type: "or", value: [targetCondition as AtomicCondition, createObjectWithCondition("data")] };
				return;
			}
			// Редактирование внутри OR-блока
			if (typeof orBlockIndex === "number" && targetCondition?.type === "or") {
				targetCondition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType, confirmationValue));
				return;
			}

			// Обычный AND-блок
			conditionBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType, confirmationValue));
		},

		// изменение значений условия запуска действия робота
		changeValuesConditionRunActionRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;
			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent]?.[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orCondition.value);
					}
				}
				return;
			}

			// Обычный AND
			if (data.type === "confirmation" || data.type === "break" || data.id !== "") {
				conditionBlock.splice(conditionIdx, 1, data);
			}
		},

		// удаление условия запуска действия робота
		deleteConditionRunActionRobot: (
			state,
			action: PayloadAction<{
				actionEvent: RobotActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;
			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.actions[actionEvent]?.[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orConditions = conditionBlock[conditionIdx];

				if (orConditions?.type === "or") {
					orConditions.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orConditions.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orConditions.value);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				actionBlock.conditions.splice(conditionBlockIdx, 1);
			}
		},

		// -------------------------------------------------------------------------
		// изменение максимального количества исключений подряд
		changeMaxNumberException: (state, action: PayloadAction<{ exception: ExceptionType; max: number }>) => {
			if (state.robot.data) {
				// если есть исключение
				if (action.payload.exception in state.robot.data.exceptions) {
					// если есть поле max - меняем значение
					if ("max" in state.robot.data.exceptions[action.payload.exception]) {
						state.robot.data.exceptions[action.payload.exception].max = action.payload.max;
					}
					// иначе создаем поле max
					else {
						state.robot.data.exceptions[action.payload.exception] = {
							...state.robot.data.exceptions[action.payload.exception],
							max: action.payload.max,
						};
					}
				}
				// иначе создаем исключение и поле max
				else {
					state.robot.data.exceptions = {
						...state.robot.data.exceptions,
						[action.payload.exception]: {
							max: action.payload.max,
						},
					};
				}
			}
		},
		// добавление исключения робота
		addingRobotException: (state, action: PayloadAction<IAddingRobotException>) => {
			if (state.robot.data) {
				// если есть исключение
				if (action.payload.exception in state.robot.data.exceptions) {
					// и если есть канал
					if (state.robot.data.exceptions[action.payload.exception].actions && state.robot.data.exceptions[action.payload.exception].actions[action.payload.channel]) {
						state.robot.data.exceptions[action.payload.exception].actions[action.payload.channel].push(action.payload.action); // добавляем действие
					}
					// если нет канала - создаем
					else {
						state.robot.data.exceptions[action.payload.exception].actions = {
							...state.robot.data.exceptions[action.payload.exception].actions,
							[action.payload.channel]: [action.payload.action],
						};
					}
				}
				// если нет события действия - создаем событие, канал
				else {
					state.robot.data.exceptions = {
						...state.robot.data.exceptions,
						[action.payload.exception]: {
							actions: {
								[action.payload.channel]: [action.payload.action],
							},
						},
					};
				}
			}
		},
		// замена действия исключения
		replaceActionExceptionRobot: (state, actionRedux: PayloadAction<{ exception: ExceptionType; channel: string; idx: number; action: ActionVariantType }>) => {
			if (state.robot.data) {
				const { exception, channel, idx, action } = actionRedux.payload;
				state.robot.data.exceptions[exception].actions[channel][idx] = action;
			}
		},
		// изменение данных действия исключения (experiment)
		changeRobotExceptionActionData: (
			state,
			actionRedux: PayloadAction<{ exception: ExceptionType; channel: string; idx: number; action: Partial<ActionVariantType> }>
		) => {
			if (state.robot.data) {
				const { exception, channel, idx, action } = actionRedux.payload;
				state.robot.data.exceptions[exception].actions[channel][idx] = {
					...state.robot.data.exceptions[exception].actions[channel][idx],
					...(action as ActionVariantType),
				};
			}
		},
		// удаление исключения робота
		deleteRobotException: (state, action: PayloadAction<{ exception: ExceptionType; channel: string; idx: number }>) => {
			state.robot.data?.exceptions[action.payload.exception].actions[action.payload.channel].splice(action.payload.idx, 1);
		},
		// изменение последовательности исключений робота
		replaceRobotExceptionList: (state, action: PayloadAction<{ exception: ExceptionType; channel: string; actionList: ActionVariantType[] }>) => {
			if (state.robot.data) {
				state.robot.data.exceptions[action.payload.exception].actions[action.payload.channel] = action.payload.actionList;
			}
		},
		// -------------------------------------------------------------------------
		// добавление данных категории исключения
		addCategoryDataExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { exception, channel, channelIdx, categoryId, categoryBlockIdx, data, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionInternal = robotData.exceptions[exception]?.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionInternal) return;

			// Инициализация categories
			if (!actionInternal.categories) {
				actionInternal.categories = { [categoryId]: [[data as CategoryCondition]] };
				return;
			}

			if (!(categoryId in actionInternal.categories)) {
				actionInternal.categories[categoryId] = [[data as CategoryCondition]];
				return;
			}

			const categoryBlocks = actionInternal.categories[categoryId];

			// Добавление в существующий categoryBlock
			if (categoryBlockIdx < categoryBlocks.length) {
				const categoryBlock = categoryBlocks[categoryBlockIdx];

				// Добавление в OR-блок
				if (typeof orBlockIndex === "number" && categoryBlock[orBlockIndex]?.type === "or") {
					(categoryBlock[orBlockIndex] as IMulticondition).value.push(data as AtomicCondition);
					return;
				}

				// Добавление в существующий AND-блок
				categoryBlock.push(data as CategoryCondition);
				return;
			}

			// Новый categoryBlock
			categoryBlocks.push([data as CategoryCondition]);
		},

		// изменение типа данных категории исключения
		changeTypeCategoryExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.robot.data) return;

			const { exception, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const actionItem = state.robot.data.exceptions?.[exception]?.actions?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionItem) return;

			const categoryBlocks = actionItem.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
			}
		},

		// изменение данных категории исключения
		changeCategoryDataExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				data: IConditionData | IConditionConfirmation | IConditionVariable | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { exception, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, data, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.exceptions[exception]?.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			const categoryBlocks = actionBlock.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, ...(condition.value as CategoryCondition[]));
					}
				}
				return;
			}

			// Обычная замена (AND)
			categoryBlock[categoryIdx] = data as CategoryCondition;
		},

		// удаление данных категории исключения
		deleteCategoryDataExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { exception, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.exceptions[exception].actions[channel][channelIdx] as IActionInternalAsk;
			if (!actionBlock?.categories) return;

			const categoryBlocks = actionBlock.categories[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = categoryBlock[categoryIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						categoryBlock.splice(
							categoryIdx,
							1,
							...(orCondition.value[0] as unknown as (IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition)[])
						);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (categoryBlock.length > 1) {
				categoryBlock.splice(categoryIdx, 1);
			} else {
				// если остался один блок — удаляем весь блок
				if (categoryBlocks.length > 1) {
					categoryBlocks.splice(categoryBlockIdx, 1);
				} else {
					// если осталась только одна категория — удаляем её полностью
					delete actionBlock.categories[categoryId];
					actionBlock.longContext = actionBlock.longContext?.filter((id) => id !== categoryId);
				}
			}
		},

		// изменение флага запроса в QAS с учетом прошлого контекста в исключении
		changeLongContextExceptionRobot: (
			state,
			action: PayloadAction<{ exception: ExceptionType; channel: string; channelIdx: number; categoryId: string; flag: boolean }>
		) => {
			if (state.robot.data) {
				const actionInternalAsk = state.robot.data.exceptions[action.payload.exception].actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk;

				// если стоит галочка - добавляем в список, иначе убираем
				if (action.payload.flag) {
					if (Array.isArray(actionInternalAsk.longContext)) actionInternalAsk.longContext.push(action.payload.categoryId);
					else actionInternalAsk.longContext = [action.payload.categoryId];
				} else {
					(state.robot.data.exceptions[action.payload.exception].actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk).longContext = (
						state.robot.data.exceptions[action.payload.exception].actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk
					).longContext?.filter((categoryId) => categoryId !== action.payload.categoryId);
				}
			}
		},
		// -------------------------------------------------------------------------
		// добавление условия запуска действия исключения робота
		addConditionRunActionExceptionRobot: (
			state,
			action: PayloadAction<{ exception: ExceptionType; channel: string; channelIdx: number; conditionBlockIdx: number; data: AtomicCondition; orBlockIndex?: number }>
		) => {
			const { exception, channel, channelIdx, conditionBlockIdx, data, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.exceptions[exception]?.actions?.[channel]?.[channelIdx];
			if (!actionBlock) return;

			// Если условий ещё нет — создаём
			if (!actionBlock.conditions) {
				actionBlock.conditions = [[data]];
				return;
			}

			const conditions = actionBlock.conditions;
			const conditionBlock = conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			conditions.push([data]);
		},

		// изменение типа условия запуска действия исключения робота
		changeTypeConditionRunActionExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const { exception, channel, channelIdx, conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.exceptions[exception]?.actions[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			const targetCondition = conditionBlock[conditionIdx];

			// Преобразование обычного условия в OR-блок
			if (conditionType === "or" && targetCondition) {
				conditionBlock[conditionIdx] = {
					type: "or",
					value: [
						targetCondition as AtomicCondition, // Существующее условие
						createObjectWithCondition("data"), // Новый "пустой" вариант для OR
					],
				};
				return;
			}

			// Редактирование внутри OR-блока
			if (typeof orBlockIndex === "number" && targetCondition?.type === "or") {
				targetCondition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));
				return;
			}

			// Обычный AND-блок
			conditionBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));
		},

		// изменение значений условия запуска действия исключения робота
		changeValuesConditionRunActionExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { exception, channel, channelIdx, conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;
			const robotData = state.robot.data;
			if (!robotData) return;

			const actionBlock = robotData.exceptions[exception]?.actions[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orCondition.value);
					}
				}
				return;
			}

			// Обычный AND
			if (data.type === "confirmation" || data.type === "break" || data.id !== "") {
				conditionBlock.splice(conditionIdx, 1, data);
			}
		},

		// удаление условия запуска действия исключения робота
		deleteConditionRunActionExceptionRobot: (
			state,
			action: PayloadAction<{
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { exception, channel, channelIdx, conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;

			if (!state.robot.data) return;

			const actionBlock = state.robot.data.exceptions[exception].actions[channel][channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orConditions = conditionBlock[conditionIdx];

				if (orConditions?.type === "or") {
					orConditions.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orConditions.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orConditions.value);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				actionBlock.conditions.splice(conditionBlockIdx, 1);
			}
		},

		// -------------------------------------------------------------------------
		// добавление сервисных данных робота
		addServiceData: (state, action: PayloadAction<{ serviceDataName: ServiceDataType; type: ServiceTypeRobot }>) => {
			if (state.robot.data) {
				state.robot.data.servicedata[action.payload.serviceDataName] = {
					type: action.payload.type,
					model: "",
					param: "",
				};
			}
		},
		// изменение сервисных данных робота
		changeServiceData: (state, action: PayloadAction<IChangeServiceData>) => {
			if (state.robot.data) {
				if (action.payload.data.field === "type") {
					state.robot.data.servicedata[action.payload.serviceDataName][action.payload.data.field] = action.payload.data.value;
				} else {
					state.robot.data.servicedata[action.payload.serviceDataName][action.payload.data.field] = action.payload.data.value;
				}
			}
		},
		// удаление сервисных данных робота
		deleteServiceData: (state, action: PayloadAction<{ serviceDataName: ServiceDataType }>) => {
			if (state.robot.data) {
				delete state.robot.data.servicedata[action.payload.serviceDataName];
			}
		},
		// -------------------------------------------------------------------------
		// изменение порога доверия в настройках модели робота
		changeThresholdInModelConfig: (state, action: PayloadAction<{ serviceType: ServiceTypeModelRobot; modelName: string; threshold: number }>) => {
			if (state.robot.data) {
				if (action.payload.serviceType === "smc") {
					state.robot.data.models.smc[action.payload.modelName].confidenceThreshold = action.payload.threshold;
				} else if (action.payload.serviceType === "see") {
					state.robot.data.models.see[action.payload.modelName].threshold = action.payload.threshold;
				}
			}
		},
		// изменение флага включения коррекции (smc)
		changeCorrectionInModelConfig: (state, action: PayloadAction<{ modelName: string; correction: CorrectionType }>) => {
			if (state.robot.data) {
				state.robot.data.models.smc[action.payload.modelName].correction = action.payload.correction;
			}
		},
		// изменение настроек голоса в настройках модели робота
		changeSettingVoiceInModelConfig: (state, action: PayloadAction<{ serviceType: ServiceTypeModelRobot; modelName: string; setting: IModelTtsConfig }>) => {
			if (state.robot.data) {
				state.robot.data.models.tts[action.payload.modelName] = action.payload.setting;
			}
		},
		// изменение модели в настройках модели робота
		changeModelnameInModelConfig: (state, action: PayloadAction<{ serviceType: ServiceTypeModelRobot; oldModelName: string; newModelName: string }>) => {
			if (state.robot.data) {
				state.robot.data.models[action.payload.serviceType][action.payload.newModelName] =
					state.robot.data.models[action.payload.serviceType][action.payload.oldModelName];
				delete state.robot.data.models[action.payload.serviceType][action.payload.oldModelName];
			}
		},
		// добавление настроек модели робота
		addModelConfig: (state, action: PayloadAction<{ serviceType: ServiceTypeModelRobot; modelName: string }>) => {
			if (state.robot.data) {
				if (action.payload.serviceType === "smc") {
					state.robot.data.models[action.payload.serviceType] = {
						...state.robot.data.models[action.payload.serviceType],
						[action.payload.modelName]: { confidenceThreshold: 0, correction: 1 },
					};
				} else if (action.payload.serviceType === "see") {
					state.robot.data.models[action.payload.serviceType] = {
						...state.robot.data.models[action.payload.serviceType],
						[action.payload.modelName]: { threshold: 0 },
					};
				} else if (action.payload.serviceType === "tts") {
					state.robot.data.models[action.payload.serviceType] = {
						...state.robot.data.models[action.payload.serviceType],
						[action.payload.modelName]: { pitch: 100, rate: 100, volume: 100, frequency: 22050 },
					};
				}
			}
		},
		// удаление настроек модели робота
		deleteModelConfig: (state, action: PayloadAction<{ serviceType: ServiceTypeModelRobot; modelName: string }>) => {
			if (state.robot.data) {
				delete state.robot.data.models[action.payload.serviceType][action.payload.modelName];
			}
		},
		// -------------------------------------------------------------------------
		// изменение времени жизни сессии робота
		changeSessionLifeTime: (state, action: PayloadAction<number>) => {
			if (state.robot.data) {
				state.robot.data.session.lifetime = action.payload;
			}
		},
		// -------------------------------------------------------------------------
		// добавление интервала робота
		addInterval: (state, action: PayloadAction<string>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload] = [TIME_INTERVAL];
			}
		},
		// изменение имени интервала
		changeNameInterval: (state, action: PayloadAction<{ oldName: string; newName: string }>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.newName] = state.robot.data.timeintervals[action.payload.oldName];
				delete state.robot.data.timeintervals[action.payload.oldName];
			}
		},
		// добавление варианта интервала
		addIntervalVariant: (state, action: PayloadAction<string>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload].push(TIME_INTERVAL);
			}
		},
		// удаление варианта интервала
		deleteIntervalVariant: (state, action: PayloadAction<{ intervalName: string; intervalIdx: number }>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.intervalName].splice(action.payload.intervalIdx, 1);
			}
		},
		// изменение дней по производственному календарю
		changeDaysInterval: (state, action: PayloadAction<{ intervalName: string; intervalIdx: number; days: DaysType }>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.intervalName][action.payload.intervalIdx].days = action.payload.days;
			}
		},
		// изменение дней недели/месяцев/дней месяца
		changeDataInterval: (
			state,
			action: PayloadAction<{ intervalName: string; intervalIdx: number; data: number[]; objectOfChange: keyof Omit<IInterval, "days" | "intervals"> }>
		) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.intervalName][action.payload.intervalIdx][action.payload.objectOfChange] = action.payload.data;
			}
		},
		// добавление времени интервала
		addTimeInterval: (state, action: PayloadAction<{ intervalName: string; intervalIdx: number }>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.intervalName][action.payload.intervalIdx].intervals.push([0, 1439]);
			}
		},
		// изменение времени интервала
		changeTimeInterval: (state, action: PayloadAction<{ intervalName: string; intervalIdx: number; data: [number, number]; idx: number }>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.intervalName][action.payload.intervalIdx].intervals[action.payload.idx] = action.payload.data;
			}
		},
		// удаление времени интервала
		deleteTimeInterval: (state, action: PayloadAction<{ intervalName: string; intervalIdx: number; idx: number }>) => {
			if (state.robot.data) {
				state.robot.data.timeintervals[action.payload.intervalName][action.payload.intervalIdx].intervals.splice(action.payload.idx, 1);
			}
		},
		// удаление интервала робота
		deleteInterval: (state, action: PayloadAction<string>) => {
			if (state.robot.data) {
				delete state.robot.data.timeintervals[action.payload];
			}
		},
		// -------------------------------------------------------------------------
		// изменение прогресса импорта робота в %
		changeProgressImportRobot: (state, action: PayloadAction<number>) => {
			state.importRobot.progress = action.payload;
		},
		// -------------------------------------------------------------------------
		// очистка state
		clearState: (state) => {
			state.robotList = initialState.robotList;
			state.robot = initialState.robot;
			state.addingRobot = initialState.addingRobot;
			state.editingRobot = initialState.editingRobot;
			state.deletingRobot = initialState.deletingRobot;
			state.commitConfigRobot = initialState.commitConfigRobot;
			state.applyingRobot = initialState.applyingRobot;
			state.restorationRobot = initialState.restorationRobot;
			state.clearDraftingRobot = initialState.clearDraftingRobot;
			state.importRobot = initialState.importRobot;
			state.exportRobot = initialState.exportRobot;
			state.searchInRobot = initialState.searchInRobot;
		},
		// очистка списка роботов
		clearRobotList: (state) => {
			state.robotList = initialState.robotList;
		},
		// очистка данных робота
		clearRobot: (state) => {
			state.robot = initialState.robot;
		},
		// очистка данных робота, кроме статусов версионности
		clearRobotExceptVersions: (state) => {
			state.robot.data = initialState.robot.data;
			state.robot.error = initialState.robot.error;
			state.robot.message = initialState.robot.message;
			state.robot.status = initialState.robot.status;
		},
		// очистка статуса добавления робота
		clearAddingRobot: (state) => {
			state.addingRobot = initialState.addingRobot;
		},
		// очистка статуса изменения данных робота
		clearEditingRobot: (state) => {
			state.editingRobot = initialState.editingRobot;
		},
		// очистка статуса удаления робота
		clearDeletingRobot: (state) => {
			state.deletingRobot = initialState.deletingRobot;
		},
		// очистка статуса применения настроек робота
		clearCommitConfigRobot: (state) => {
			state.commitConfigRobot = initialState.commitConfigRobot;
		},
		// очистка статуса применения робота в прод
		clearApplyingRobot: (state) => {
			state.applyingRobot = initialState.applyingRobot;
		},
		// очистка статуса восстановления модели
		clearRestorationRobot: (state) => {
			state.restorationRobot = initialState.restorationRobot;
		},
		// очистка статуса обнуления черновика продовым роботом
		clearClearDraftingRobot: (state) => {
			state.clearDraftingRobot = initialState.clearDraftingRobot;
		},
		// очистка статуса импорта робота
		clearImportRobot: (state) => {
			state.importRobot = initialState.importRobot;
		},
		// очистка статуса импорта робота
		clearExportRobot: (state) => {
			state.exportRobot = initialState.exportRobot;
		},
		// очистка данных глобального поиска по сценарию
		clearSearchInRobot: (state) => {
			state.searchInRobot = initialState.searchInRobot;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getRobotList.pending, (state) => {
				state.robotList.status = RequestStatus.LOADING;
			})
			.addCase(getRobotList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.robotList.status = RequestStatus.IDLE;
					state.robotList.data = action.payload.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
				} else state.robotList.status = RequestStatus.FAILED;
			})
			.addCase(getRobotList.rejected, (state, action: PayloadAction<unknown>) => {
				state.robotList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.robotList.error = error;
				state.robotList.message = message;
			})
			.addCase(getRobot.pending, (state) => {
				state.robot.status = RequestStatus.LOADING;
			})
			.addCase(getRobot.fulfilled, (state, action) => {
				if (action.payload && "data" in action.payload) {
					state.robot.status = RequestStatus.IDLE;
					state.robot.data = action.payload.data;
				} else state.robot.status = RequestStatus.FAILED;
			})
			.addCase(getRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.robot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.robot.error = error;
				state.robot.message = message;
			})
			.addCase(addRobot.pending, (state) => {
				state.addingRobot.status = RequestStatus.LOADING;
			})
			.addCase(addRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingRobot.status = RequestStatus.IDLE;
					state.addingRobot.error = action.payload.error;
					state.addingRobot.message = action.payload.message;
					if (action.payload.id) {
						state.addingRobot.id = action.payload.id;
					}
				} else state.addingRobot.status = RequestStatus.FAILED;
			})
			.addCase(addRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingRobot.error = error;
				state.addingRobot.message = message;
			})
			.addCase(editRobot.pending, (state) => {
				state.editingRobot.status = RequestStatus.LOADING;
			})
			.addCase(editRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingRobot.status = RequestStatus.IDLE;
					state.editingRobot.error = action.payload.error;
					state.editingRobot.message = action.payload.message;
				} else state.editingRobot.status = RequestStatus.FAILED;
			})
			.addCase(editRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingRobot.error = error;
				state.editingRobot.message = message;
			})
			.addCase(deleteRobot.pending, (state) => {
				state.deletingRobot.status = RequestStatus.LOADING;
			})
			.addCase(deleteRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingRobot.status = RequestStatus.IDLE;
					state.deletingRobot.error = action.payload.error;
					state.deletingRobot.message = action.payload.message;
				} else state.deletingRobot.status = RequestStatus.FAILED;
			})
			.addCase(deleteRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingRobot.error = error;
				state.deletingRobot.message = message;
			})
			.addCase(commitConfigRobot.pending, (state) => {
				state.commitConfigRobot.status = RequestStatus.LOADING;
			})
			.addCase(commitConfigRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.commitConfigRobot.status = RequestStatus.IDLE;
					state.commitConfigRobot.error = action.payload.error;
					state.commitConfigRobot.message = action.payload.message;
				} else state.commitConfigRobot.status = RequestStatus.FAILED;
			})
			.addCase(commitConfigRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.commitConfigRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.commitConfigRobot.error = error;
				state.commitConfigRobot.message = message;
			})
			.addCase(applyRobot.pending, (state) => {
				state.applyingRobot.status = RequestStatus.LOADING;
			})
			.addCase(applyRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.applyingRobot.status = RequestStatus.IDLE;
					state.applyingRobot.error = action.payload.error;
					state.applyingRobot.message = action.payload.message;
				} else state.applyingRobot.status = RequestStatus.FAILED;
			})
			.addCase(applyRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.applyingRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.applyingRobot.error = error;
				state.applyingRobot.message = message;
			})
			.addCase(restoreRobot.pending, (state) => {
				state.restorationRobot.status = RequestStatus.LOADING;
			})
			.addCase(restoreRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.restorationRobot.status = RequestStatus.IDLE;
					state.restorationRobot.error = action.payload.error;
					state.restorationRobot.message = action.payload.message;
				} else state.restorationRobot.status = RequestStatus.FAILED;
			})
			.addCase(restoreRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.restorationRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.restorationRobot.error = error;
				state.restorationRobot.message = message;
			})
			.addCase(clearDraftRobot.pending, (state) => {
				state.clearDraftingRobot.status = RequestStatus.LOADING;
			})
			.addCase(clearDraftRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.clearDraftingRobot.status = RequestStatus.IDLE;
					state.clearDraftingRobot.error = action.payload.error;
					state.clearDraftingRobot.message = action.payload.message;
				} else state.clearDraftingRobot.status = RequestStatus.FAILED;
			})
			.addCase(clearDraftRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.clearDraftingRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.clearDraftingRobot.error = error;
				state.clearDraftingRobot.message = message;
			})
			.addCase(importRobot.pending, (state) => {
				state.importRobot.status = RequestStatus.LOADING;
			})
			.addCase(importRobot.fulfilled, (state, action) => {
				if (action.payload) {
					state.importRobot.status = RequestStatus.IDLE;
					state.importRobot.error = action.payload.error;
					state.importRobot.message = action.payload.message;
				} else state.importRobot.status = RequestStatus.FAILED;
			})
			.addCase(importRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.importRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.importRobot.error = error;
				state.importRobot.message = message;
			})
			.addCase(exportRobot.pending, (state) => {
				state.exportRobot.status = RequestStatus.LOADING;
			})
			.addCase(exportRobot.fulfilled, (state, action) => {
				state.exportRobot.status = RequestStatus.IDLE;
				if (action.payload) {
					state.exportRobot.error = action.payload.error;
					state.exportRobot.message = action.payload.message;
				}
			})
			.addCase(exportRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.exportRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.exportRobot.error = error;
				state.exportRobot.message = message;
			})
			.addCase(searchInRobot.pending, (state) => {
				state.searchInRobot.status = RequestStatus.LOADING;
			})
			.addCase(searchInRobot.fulfilled, (state, action) => {
				if (action.payload && "result" in action.payload && action.payload.result) {
					state.searchInRobot.status = RequestStatus.IDLE;
					state.searchInRobot.data = action.payload.result;
				} else state.searchInRobot.status = RequestStatus.FAILED;
			})
			.addCase(searchInRobot.rejected, (state, action: PayloadAction<unknown>) => {
				state.searchInRobot.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.searchInRobot.error = error;
				state.searchInRobot.message = message;
			});
	},
});

export const {
	addingActiveRobotId,
	changeActiveRobotVersion,
	addingRobotPossibleVersions,
	changeRobotPossibleVersionsInList,
	editRobotName,
	addingRobotAction,
	addServiceData,
	changeServiceData,
	deleteServiceData,
	changeThresholdInModelConfig,
	changeCorrectionInModelConfig,
	changeSettingVoiceInModelConfig,
	changeModelnameInModelConfig,
	addModelConfig,
	deleteModelConfig,
	changeSessionLifeTime,
	addInterval,
	changeNameInterval,
	addIntervalVariant,
	deleteIntervalVariant,
	changeDaysInterval,
	changeDataInterval,
	addTimeInterval,
	changeTimeInterval,
	deleteTimeInterval,
	deleteInterval,
	replaceActionRobot,
	changeRobotEventActionData,
	deleteActionRobot,
	replaceRobotActionList,
	changeRobotExceptionActionData,
	replaceRobotExceptionList,
	addCategoryDataExceptionRobot,
	changeTypeCategoryExceptionRobot,
	changeCategoryDataExceptionRobot,
	deleteCategoryDataExceptionRobot,
	changeLongContextExceptionRobot,
	addingRobotException,
	addCategoryDataRobot,
	changeTypeCategoryRobot,
	changeCategoryDataRobot,
	deleteCategoryDataRobot,
	changeLongContextRobot,
	addConditionRunActionRobot,
	changeTypeConditionRunActionRobot,
	changeValuesConditionRunActionRobot,
	deleteConditionRunActionRobot,
	changeMaxNumberException,
	replaceActionExceptionRobot,
	deleteRobotException,
	addConditionRunActionExceptionRobot,
	changeTypeConditionRunActionExceptionRobot,
	changeValuesConditionRunActionExceptionRobot,
	deleteConditionRunActionExceptionRobot,
	changeProgressImportRobot,
	clearState,
	clearRobotList,
	clearRobot,
	clearRobotExceptVersions,
	clearAddingRobot,
	clearEditingRobot,
	clearDeletingRobot,
	clearCommitConfigRobot,
	clearApplyingRobot,
	clearRestorationRobot,
	clearClearDraftingRobot,
	clearImportRobot,
	clearExportRobot,
	clearSearchInRobot,
} = sesRobotSlice.actions;

export const selectRobotList = (state: RootState) => state.sesRobot.robotList;
export const selectActiveRobotId = (state: RootState) => state.sesRobot.robotList.activeRobotId;
export const selectActiveRobotVersion = (state: RootState) => state.sesRobot.robotList.activeRobotVersion;
export const selectRobot = (state: RootState) => state.sesRobot.robot;
export const selectAddingRobot = (state: RootState) => state.sesRobot.addingRobot;
export const selectEditingRobot = (state: RootState) => state.sesRobot.editingRobot;
export const selectDeletingRobot = (state: RootState) => state.sesRobot.deletingRobot;
export const selectCommitConfigRobot = (state: RootState) => state.sesRobot.commitConfigRobot;
export const selectApplyingRobot = (state: RootState) => state.sesRobot.applyingRobot;
export const selectRestorationRobot = (state: RootState) => state.sesRobot.restorationRobot;
export const selectClearDraftingRobot = (state: RootState) => state.sesRobot.clearDraftingRobot;
export const selectImportRobot = (state: RootState) => state.sesRobot.importRobot;
export const selectExportRobot = (state: RootState) => state.sesRobot.exportRobot;
export const selectSearchInRobot = (state: RootState) => state.sesRobot.searchInRobot;

export default sesRobotSlice.reducer;
